import React from 'react';
import { Link } from 'components/link';
import { routesNO, RouteList, routesEN } from 'api/routes';
import styles from './subscription-details.module.scss';
import { Subscription } from '_types/subscription';
import { formatDate } from 'utils/date-utils';
import { Language } from '_types/language';
import { useSelector } from 'react-redux';
import { languageSelector } from 'store-modules/app-settings';
import {
    returnCopyForLanguage,
    returnRoutesForLanguage,
} from 'services/language-service';
import { ProSub } from 'professional/components/pro-sub';
import { proAccountSelector } from 'store-modules/professional';
import { userSelector } from 'store-modules/user';
import classNames from 'classnames';
import { ProOwner } from 'professional/components/pro-owner';

interface Props {
    sub: Subscription;
}
interface ViewCopy {
    linkEndSubscription: string;
    textNextPayment: string;
    addProCodeLabel: string;
}

const nbCopy: ViewCopy = {
    linkEndSubscription: 'Avslutt abonnement',
    textNextPayment: 'Neste betaling er',
    addProCodeLabel: 'Legg til FuelBox bedriftskode',
};

const enCopy: ViewCopy = {
    linkEndSubscription: 'Cancel subscription',
    textNextPayment: 'Next payment is due',
    addProCodeLabel: 'Add a company code',
};

const copies: { name: Language; copy: ViewCopy }[] = [
    {
        name: 'nb',
        copy: nbCopy,
    },
    {
        name: 'en',
        copy: enCopy,
    },
];

const routeObj: { name: Language; routes: RouteList }[] = [
    {
        name: 'nb',
        routes: routesNO,
    },
    {
        name: 'en',
        routes: routesEN,
    },
];

const ActiveSub: React.FC<Props> = ({ sub }: Props) => {
    const language: Language = useSelector(languageSelector);
    const user = useSelector(userSelector);
    const organization = useSelector(proAccountSelector);
    const { linkEndSubscription, textNextPayment, addProCodeLabel } =
        returnCopyForLanguage(language, copies);
    const routes = returnRoutesForLanguage(language, routeObj);
    const { productName, next_payment_date } = sub.data.subscription;
    const proSubActive =
        (!!organization && organization.name === user?.organization) ||
        sub.data.pro_content_disabled;

    const nextPaymentDate = formatDate(next_payment_date);
    const isProOwner = user?.isCompanyAdmin;

    const premiumSubInfo = !proSubActive && !isProOwner && (
        <div>
            <h2 className={styles.productName}>{productName}</h2>
            <p>
                {textNextPayment} {nextPaymentDate}
            </p>
        </div>
    );

    const proContent = !!proSubActive && <ProSub />;
    const proOwnerContent = !!isProOwner && <ProOwner />;

    const cancelLink = !proSubActive && !isProOwner && (
        <Link
            to={routes.endSubscription}
            className={!!proSubActive ? styles.proLink : styles.link}
            block={true}
            highlight={true}
        >
            {linkEndSubscription}
        </Link>
    );

    const addProCodeLink = !user?.organization &&
        !isProOwner &&
        !proSubActive && (
            <Link
                to={routes.proCodeSubPage}
                className={classNames(styles.link, styles.addProCodeLink)}
                block={true}
                highlight={false}
            >
                {addProCodeLabel}
            </Link>
        );

    return (
        <React.Fragment>
            {premiumSubInfo}
            {isProOwner ? proOwnerContent : proContent}
            {cancelLink}
            {addProCodeLink}
        </React.Fragment>
    );
};

export default ActiveSub;
